import React, { useEffect, useState } from "react";
import axiosInstance from "../utils/axiosInstance";
import "../style/InvoiceList.css";
import { Link } from "react-router-dom";
import PaginationControls from "./PaginationControls";
import { formatNumber } from "../utils/numberFormat";
import { formatGregorianDate } from "../utils/dateFormat";

function InvoiceList() {
  const [invoices, setInvoices] = useState([]);
  const [count, setCount] = useState(0);
  const [next, setNext] = useState(null);
  const [previous, setPrevious] = useState(null);

  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  // SINGLE selection
  const [selectedId, setSelectedId] = useState(null);

  // download state
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [templateError, setTemplateError] = useState("");
  const [actionError, setActionError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  // Server-side Search & Pagination
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [5, 10, 20, 50, 100];

  useEffect(() => {
    let active = true;
    axiosInstance
      .get(`documents/templates/`)
      .then((res) => {
        if (active) {
          setTemplates(Array.isArray(res.data) ? res.data : res.data?.results || []);
          setTemplateError("");
        }
      })
      .catch(() => {
        if (active) setTemplateError("قالب‌های گواهی مبدأ بارگذاری نشدند.");
      });
    return () => { active = false; };
  }, [retryKey]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setListError("");
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.append("search", searchTerm.trim());
    params.append("page", currentPage);
    params.append("page_size", pageSize);

    axiosInstance
      .get(`documents/invoices/?${params.toString()}`)
      .then((res) => {
        if (!active) return;
        const results = res.data.results || [];
        setInvoices(results);
        setCount(res.data.count || 0);
        setNext(res.data.next || null);
        setPrevious(res.data.previous || null);

        // If the selected row is not in the current page, clear selection
        setSelectedId((current) =>
          current && results.some((row) => row.id === current) ? current : null
        );
      })
      .catch(() => {
        if (active) setListError("فهرست فاکتورها بارگذاری نشد. دوباره تلاش کنید.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => { active = false; };
  }, [searchTerm, currentPage, pageSize, retryKey]);

  const totalPages = Math.ceil(count / pageSize);
  const hasNext = !!next;
  const hasPrevious = !!previous;

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  const handlePageChange = (e) => setCurrentPage(Number(e.target.value));
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };
  const handleNextPage = () => hasNext && setCurrentPage((p) => p + 1);
  const handlePreviousPage = () => hasPrevious && setCurrentPage((p) => p - 1);

  // Extract filename from Content-Disposition (if server sends it)
  const filenameFromDisposition = (disposition, fallback) => {
    if (!disposition) return fallback;
    const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(
      disposition
    );
    try {
      const name = decodeURIComponent(match?.[1] || match?.[2] || "");
      return name || fallback;
    } catch {
      return fallback;
    }
  };

  // helpers: fetch blob and trigger download
  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const getAndDownload = async (url, fallbackName) => {
    const res = await axiosInstance.get(url, { responseType: "blob" });
    const contentType = res.headers?.["content-type"] || "application/pdf";
    const blob = new Blob([res.data], { type: contentType });
    const cd = res.headers?.["content-disposition"];
    downloadBlob(blob, filenameFromDisposition(cd, fallbackName));
  };

  // ACTIONS (single row)
  const actionDownloadInvoice = async () => {
    if (!selectedId) return;
    try {
      setActionError("");
      setDownloading(true);
      await getAndDownload(
        `documents/invoices/${selectedId}/pdf/`,
        `invoice_${selectedId}.pdf`
      );
    } catch {
      setActionError("دانلود اینوویس ناموفق بود. دوباره تلاش کنید.");
    } finally {
      setDownloading(false);
    }
  };

  const actionDownloadPacking = async () => {
    if (!selectedId) return;
    try {
      setActionError("");
      setDownloading(true);
      await getAndDownload(
        `documents/packing/${selectedId}/pdf/`,
        `packing_${selectedId}.pdf`
      );
    } catch {
      setActionError("دانلود پکینگ ناموفق بود. دوباره تلاش کنید.");
    } finally {
      setDownloading(false);
    }
  };

  const actionDownloadOriginCert = async () => {
    if (!selectedId) return;
    if (!selectedTemplate) {
      setActionError("لطفاً یک قالب برای گواهی مبدأ انتخاب کنید.");
      return;
    }
    try {
      setActionError("");
      setDownloading(true);
      const res = await axiosInstance.post(
        `documents/origin-cert/`,
        { template_id: Number(selectedTemplate), invoice_id: selectedId },
        { responseType: "blob" }
      );
      const blob = new Blob([res.data], { type: "application/pdf" });
      const cd = res.headers?.["content-disposition"];
      const name = filenameFromDisposition(
        cd,
        `certificate_of_origin_${selectedId}.pdf`
      );
      downloadBlob(blob, name);
    } catch {
      setActionError("دانلود گواهی مبدأ ناموفق بود. دوباره تلاش کنید.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="invoice-list-container mt-8 font-vazir">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row mt-10 md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold">فهرست فاکتور‌ها</h2>
          <p className="text-sm text-gray-500">
            {count?.toLocaleString?.() ?? count} فاکتور ثبت شده
          </p>
        </div>

        <div className="flex gap-2">
          <Link to="/invoices/new" className="rounded-xl bg-blue-600 px-4 py-2 text-center text-white transition hover:bg-blue-700">
            فاکتور جدید
          </Link>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between rounded-2xl border p-3 bg-white shadow-sm">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center md:flex-1">
          <input
            type="text"
            aria-label="جستجوی فاکتور"
            placeholder="جستجوی فاکتور..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full md:w-72 rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          {/* Template select used by Origin Cert */}
          <select
            className="w-full rounded-xl border bg-white px-3 py-2 sm:w-auto"
            value={selectedTemplate}
            onChange={(e) => setSelectedTemplate(e.target.value)}
            title="انتخاب قالب گواهی مبدأ"
            disabled={!selectedId}
          >
            <option value="">قالب گواهی مبدأ…</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        {/* 3 Buttons */}
        <div className="grid grid-cols-1 gap-2 sm:flex sm:flex-wrap">
          <button
            onClick={actionDownloadInvoice}
            disabled={downloading || !selectedId}
            className={`px-4 py-2 rounded-xl border transition ${
              downloading || !selectedId
                ? "bg-teal-300 text-white cursor-not-allowed"
                : "bg-teal-600 hover:bg-teal-700 text-white"
            }`}
            title="دانلود PDF اینوویس برای ردیف انتخاب‌شده"
          >
            دانلود اینوویس
          </button>

          <button
            onClick={actionDownloadPacking}
            disabled={downloading || !selectedId}
            className={`px-4 py-2 rounded-xl border transition ${
              downloading || !selectedId
                ? "bg-zinc-300 text-white cursor-not-allowed"
                : "bg-zinc-500 text-white hover:bg-zinc-600"
            }`}
            title="دانلود PDF پکینگ برای ردیف انتخاب‌شده"
          >
            دانلود پکینگ
          </button>

          <button
            onClick={actionDownloadOriginCert}
            disabled={downloading || !selectedId || !selectedTemplate}
            className={`px-4 py-2 rounded-xl text-white transition ${
              downloading || !selectedId || !selectedTemplate
                ? "bg-blue-300 cursor-not-allowed"
                : "bg-blue-600 hover:bg-blue-700"
            }`}
            title="دانلود گواهی مبدأ (نیازمند انتخاب قالب)"
          >
            گواهی مبدأ
          </button>
        </div>
      </div>

      {templateError && (
        <div role="alert" className="mt-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          {templateError}
          <button type="button" className="mr-3 font-semibold underline" onClick={() => setRetryKey((key) => key + 1)}>
            تلاش دوباره
          </button>
        </div>
      )}
      {actionError && (
        <p role="alert" className="mt-3 rounded-xl border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {actionError}
        </p>
      )}

      {listError ? (
        <div role="alert" className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-rose-800">
          <p>{listError}</p>
          <button type="button" onClick={() => setRetryKey((key) => key + 1)} className="mt-3 rounded-lg bg-rose-700 px-4 py-2 text-sm text-white">
            تلاش دوباره
          </button>
        </div>
      ) : loading ? (
        <p role="status" className="mt-4 rounded-2xl border bg-white p-6 text-center text-slate-600">
          در حال بارگذاری فاکتورها...
        </p>
      ) : (
      <>
      {/* Selection hint */}
      <div className="mt-2 text-sm text-gray-600">
        {selectedId
          ? `ردیف انتخاب‌شده: ${selectedId}`
          : "هیچ ردیفی انتخاب نشده است"}
      </div>

      {invoices.length === 0 ? (
        <p className="mt-4 rounded-2xl border bg-white p-6 text-center text-slate-500 md:hidden">
          نتیجه‌ای یافت نشد.
        </p>
      ) : (
        <div className="mt-4 space-y-3 md:hidden">
          {invoices.map((inv) => (
            <article key={inv.id} className={`rounded-2xl border bg-white p-4 shadow-sm ${selectedId === inv.id ? "border-blue-500" : "border-slate-200"}`}>
              <label className="flex items-center gap-3 font-semibold text-slate-900">
                <input type="radio" name="invoiceRowMobile" checked={selectedId === inv.id} onChange={() => setSelectedId(inv.id)} />
                انتخاب فاکتور <bdi>{inv.invoice_number}</bdi>
              </label>
              <dl className="mt-3 grid grid-cols-2 gap-2 text-sm">
                <dt className="text-slate-500">ثبت سفارش</dt><dd dir="ltr" className="text-left">{inv.proforma_details?.prf_order_no || "—"}</dd>
                <dt className="text-slate-500">کوتاژ</dt><dd dir="ltr" className="text-left">{inv.cottage || "—"}</dd>
                <dt className="text-slate-500">مبلغ کل</dt><dd className="text-left"><bdi dir="ltr">{formatNumber(inv.total_amount, { minimumFractionDigits: 2 })} {inv.invoice_currency || ""}</bdi></dd>
                <dt className="text-slate-500">تاریخ شمسی</dt><dd className="text-left"><bdi dir="ltr">{formatGregorianDate(inv.invoice_date)}</bdi></dd>
              </dl>
              <Link className="mt-4 inline-flex rounded-lg bg-blue-50 px-3 py-2 text-sm font-semibold text-blue-700" to={`/invoices/details/${inv.invoice_id}`}>
                مشاهده جزئیات
              </Link>
            </article>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="mt-4 hidden w-full overflow-x-auto rounded-2xl border bg-white shadow-sm md:block">
        <table className="w-full min-w-[960px] text-sm">
          <thead className="bg-gray-50">
            <tr className="text-right">
              <th className="p-3 w-10">انتخاب</th>
              <th className="p-3">ردیف</th>
              <th className="p-3">شماره اظهارنامه</th>
              <th className="p-3">شماره ثبت سفارش</th>
              <th className="p-3">شماره فاکتور</th>
              <th className="p-3">مبلغ کل</th>
              <th className="p-3">هزینه حمل</th>
              <th className="p-3">واحد پول</th>
              <th className="p-3">تاریخ شمسی</th>
              <th className="p-3">جزئیات</th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv, index) => (
              <tr
                key={inv.id}
                className={`border-t hover:bg-gray-50/60 transition ${
                  selectedId === inv.id ? "bg-blue-50/60" : ""
                }`}
              >
                <td className="p-3">
                  <input
                    type="radio"
                    name="invoiceRowDesktop"
                    checked={selectedId === inv.id}
                    onChange={() => setSelectedId(inv.id)}
                    aria-label={`انتخاب فاکتور ${inv.invoice_number}`}
                  />
                </td>
                <td className="p-3">
                  {index + 1 + (currentPage - 1) * pageSize}
                </td>
                <td className="p-3">
                  <Link
                    className="text-sky-700 border-b border-sky-700"
                    to={`/cottages/${inv.cottage}`}
                  >
                    {inv.cottage}
                  </Link>
                </td>
                <td className="p-3">
                  <Link
                    className="text-sky-700 border-b border-sky-700"
                    to={`/order-details/${inv.proforma_details?.prfVCodeInt}`}
                  >
                    {inv.proforma_details?.prf_order_no}
                  </Link>
                </td>
                <td className="p-3">{inv.invoice_number}</td>
                <td className="p-3 tabular-nums"><bdi dir="ltr">{formatNumber(inv.total_amount, { minimumFractionDigits: 2 })}</bdi></td>
                <td className="p-3 tabular-nums"><bdi dir="ltr">{formatNumber(inv.freight_charges, { minimumFractionDigits: 2 })}</bdi></td>
                <td className="p-3"><bdi dir="ltr">{inv.invoice_currency}</bdi></td>
                <td className="whitespace-nowrap p-3"><bdi dir="ltr">{formatGregorianDate(inv.invoice_date)}</bdi></td>
                <td className="p-3">
                  <Link
                    className="text-sky-700 border-b border-sky-700"
                    to={`/invoices/details/${inv.invoice_id}`}
                  >
                    جزئیات
                  </Link>
                </td>
              </tr>
            ))}

            {invoices.length === 0 && (
              <tr>
                <td className="p-6 text-center text-gray-500" colSpan={10}>
                  نتیجه‌ای یافت نشد.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4">
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          pageSizeOptions={pageSizeOptions}
          hasNext={hasNext}
          hasPrevious={hasPrevious}
          onPageChange={handlePageChange}
          onNextPage={handleNextPage}
          onPreviousPage={handlePreviousPage}
          onPageSizeChange={handlePageSizeChange}
        />
      </div>
      </>
      )}
    </div>
  );
}

export default InvoiceList;
